import { usePageInfo } from 'app/frontend/base/config/queries'
import { Check, X } from 'lucide-react'
import { ColumnDefWithFilters, ColumnGroup } from '../types'
import { useSimpleColumns } from '../utils'

const booleanCell = (value: string | number) =>
  value === 'yes' ? (
    <Check className='w-4 h-4 text-success-foreground' />
  ) : value === 'no' ? (
    <X className='w-4 h-4 text-destructive-foreground' />
  ) : null

export const useCollectionsColumns = (): ColumnDefWithFilters => {
  const {
    data: { gameVersion },
  } = usePageInfo()

  const isLive = gameVersion === 'live'
  const isClassicProgression = gameVersion === 'classic_progression'

  const collectionGroups: ColumnGroup[] = [
    {
      id: 'achievements',
      header: 'Achievements',
      subColumns: [
        {
          id: 'total_achievements',
          header: 'Total',
          field: 'achievement_points',
        },
      ],
    },
    {
      id: 'mounts',
      header: 'Mounts',
      subColumns: [
        {
          id: 'total_mounts',
          header: 'Total',
          field: isClassicProgression ? 'mounts_obtained' : 'mounts',
        },
        ...(isLive
          ? [
              {
                id: 'lura_mount',
                header: "L'ura",
                field: 'midnight_falls_mount',
                cellWrapper: booleanCell,
              },
              {
                id: 'ulatek_mount',
                header: "Ula'tek",
                field: 'ulatek_mount',
                cellWrapper: booleanCell,
              },
            ]
          : []),
      ],
    },
    ...(isLive
      ? [
          {
            id: 'pets',
            header: 'Pets',
            subColumns: [
              { id: 'total_pets', header: 'Total', field: 'unique_pets' },
              {
                id: 'lvl_25_pets',
                header: () => (
                  <span className='whitespace-nowrap'>Level 25</span>
                ),
                field: 'lvl_25_pets',
              },
            ],
          },
          {
            id: 'toys',
            header: 'Toys',
            subColumns: [
              { id: 'total_toys', header: 'Total', field: 'toys_owned' },
            ],
          },
          {
            id: 'titles',
            header: 'Titles',
            subColumns: [
              { id: 'total_titles', header: 'Total', field: 'titles' },
            ],
          },
        ]
      : []),
    {
      id: 'quests',
      header: 'Quests',
      rankFields: [
        'quests_done_total',
        'dailies_done_total',
        ...(isLive ? ['wqs_done_total'] : []),
      ],
      subColumns: [
        { id: 'regular_quests', header: 'Regular', field: 'quests_done_total' },
        { id: 'daily_quests', header: 'Daily', field: 'dailies_done_total' },
        ...(isLive
          ? [{ id: 'world_quests', header: 'World', field: 'wqs_done_total' }]
          : []),
      ],
    },
  ]

  const columns = useSimpleColumns(collectionGroups)

  return { columns, initialSortBy: [{ id: 'total_achievements', desc: true }] }
}
